<template>
  <div class="operation-observation-form">

    <!-- Errore: tipo non valido -->
    <div v-if="!isValidType" class="step-container">
      <div class="invalid-type-error">
        <h3>{{ t('operationObservationForm.invalidTypeTitle') }}</h3>
        <p>{{ t('operationObservationForm.invalidTypeValue', { type }) }}</p>
        <p>{{ t('operationObservationForm.invalidTypeValid') }} <strong>crop_operation</strong>, <strong>observation</strong></p>
      </div>
    </div>

    <!-- Step 1: Selezione tipologia -->
    <div v-if="isValidType && currentStep === 'type'" class="step-container">
      <h3>{{ t('operationObservationForm.selectType', { typeLabel }) }}</h3>
      <div class="type-list">
        <AeCard
          v-for="item in filteredTypeOptions"
          :key="item.id"
          hoverable
          @click="selectType(item)"
          class="type-card"
        >
          <h4>{{ translateSchemaName(item.name) }}</h4>
          <p v-if="item.description">{{ item.description }}</p>
        </AeCard>
      </div>
    </div>

    <!-- Step 2: Selezione Campo -->
    <div v-if="isValidType && currentStep === 'dateField'" class="step-container">
      <div class="step-header-row">
        <h3>{{ t('operationObservationForm.selectField') }}</h3>
        <AeButton v-if="selectedType" variant="secondary" size="small" @click="currentStep = 'type'">
          {{ t('operationObservationForm.changeType', { typeLabel }) }}
        </AeButton>
      </div>
      <p class="type-info" v-if="selectedType">
        {{ typeLabel }}: <strong>{{ translateSchemaName(selectedType.name) }}</strong>
      </p>
      <p class="season-info" v-if="seasonName">{{ t('operationObservationForm.season') }}: <strong>{{ seasonName }}</strong></p>
      <div class="form-section">
        <div class="form-group">
          <label>{{ t('operationObservationForm.fieldSelectLabel') }}</label>

          <div v-if="availableCrops.length > 0" class="crop-filter-buttons">
            <button
              v-for="crop in availableCrops"
              :key="crop"
              type="button"
              class="crop-filter-btn"
              :class="{ active: isCropFullySelected(crop) }"
              @click="toggleCropFields(crop)"
            >
              {{ translateCropName(crop) }}
            </button>
            <button
              type="button"
              class="crop-filter-btn"
              :class="{ active: formData.id_fields.length === filteredFieldOptions.length && filteredFieldOptions.length > 0 }"
              @click="toggleAllFields"
            >
              {{ t('operationObservationForm.allFields') }}
            </button>
          </div>

          <div class="fields-table-container">
            <table class="fields-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input
                      type="checkbox"
                      :checked="formData.id_fields.length === filteredFieldOptions.length && filteredFieldOptions.length > 0"
                      :indeterminate="formData.id_fields.length > 0 && formData.id_fields.length < filteredFieldOptions.length"
                      @change="toggleAllFields"
                    />
                  </th>
                  <th>{{ t('operationObservationForm.colField') }}</th>
                  <th>{{ t('operationObservationForm.colCrop') }}</th>
                  <th class="col-area">{{ t('operationObservationForm.colAreaHa') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="field in filteredFieldOptions"
                  :key="field.id_field"
                  :class="{ selected: formData.id_fields.includes(field.id_field) }"
                  @click="toggleField(field.id_field)"
                >
                  <td class="col-check">
                    <input
                      type="checkbox"
                      :checked="formData.id_fields.includes(field.id_field)"
                      @click.stop="toggleField(field.id_field)"
                    />
                  </td>
                  <td>{{ field.name || t('operationObservationForm.notAvailable') }}</td>
                  <td>{{ translateCropName(field.crop?.name || field.crop?.crop_name) || '' }}</td>
                  <td class="col-area">{{ formatArea(field.plot?.area || field.plot?.surface) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="fields-summary">
            <span>{{ t('operationObservationForm.fieldsSelectedCount', { count: formData.id_fields.length }) }}</span>
            <span v-if="selectedArea > 0" class="area-total">
              {{ t('operationObservationForm.totalSurface') }}: <strong>{{ formatArea(selectedArea) }} ha</strong>
            </span>
          </div>

          <!-- Riduzione superficie trattata -->
          <div v-if="formData.id_fields.length > 0" class="surface-perc-row">
            <label class="surface-perc-toggle">
              <input type="checkbox" v-model="surfacePercEnabled" @change="onSurfacePercToggle" />
              {{ t('operationObservationForm.treatPartialSurface') }}
            </label>
            <div v-if="surfacePercEnabled" class="surface-perc-input">
              <select v-model="surfaceInputMode" @change="onSurfaceModeChange" class="form-control surface-mode-select">
                <option value="perc">%</option>
                <option value="ha">ha</option>
              </select>
              <input
                v-if="surfaceInputMode === 'perc'"
                type="number"
                :value="formData.surface_perc ?? 50"
                @input="onSurfacePercInput"
                min="1" max="99" step="1"
                class="form-control surface-perc-field"
              />
              <input
                v-else
                type="number"
                :value="surfaceHaInput ?? totalAreaHa.toFixed(2)"
                @input="onSurfaceHaInput"
                min="0.01" :max="(totalAreaMqGross / 10000).toFixed(2)" step="0.01"
                class="form-control surface-perc-field"
              />
              <span class="surface-perc-hint">
                {{ t('operationObservationForm.surfacePercHint', { ha: totalAreaHa.toFixed(2), perc: formData.surface_perc ?? 50, totalHa: (totalAreaMqGross / 10000).toFixed(2) }) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Form dinamico -->
    <div v-if="isValidType && currentStep === 'form'" class="step-container">
      <h3>{{ translateSchemaName(selectedType?.name) }}</h3>
      <div class="form-info-row">
        <p class="form-info">
          {{ t('operationObservationForm.fields') }}: {{ selectedFieldNames }}
          <span v-if="totalAreaMqGross > 0" class="area-total">
            — {{ t('operationObservationForm.surface') }}: <strong>{{ totalAreaHa.toFixed(2) }} ha</strong>
            <template v-if="surfacePercEnabled && formData.surface_perc">
              <span class="area-reduced"> {{ t('operationObservationForm.surfacePercOf', { perc: formData.surface_perc, totalHa: (totalAreaMqGross / 10000).toFixed(2) }) }}</span>
            </template>
          </span>
        </p>
        <AeButton variant="secondary" size="small" @click="currentStep = 'dateField'">
          {{ t('operationObservationForm.changeFields') }}
        </AeButton>
      </div>


      <div class="form-body">
        <div class="ae-form-row">
          <label>{{ t('operationObservationForm.date') }}</label>
          <input type="date" v-model="formData.date" class="form-control" required />
        </div>

        <!-- Warning data futura per observation -->
        <div v-if="props.type === 'observation' && isFutureDate" class="future-date-warning">
          <strong>{{ t('operationObservationForm.warning') }}:</strong> {{ t('operationObservationForm.futureDateWarning') }}
        </div>

        <!-- Banner auto-pianificazione in insert (operation con data futura) -->
        <div v-if="props.type === 'crop_operation' && !isEdit && formData.status === 'planned'" class="status-banner status-planned">
          <strong>{{ t('operationObservationForm.plannedActivity') }}</strong> — {{ t('operationObservationForm.plannedActivityHint') }}
        </div>

        <!-- Ciclo di conclusione in edit (solo se status = planned) -->
        <div v-if="props.type === 'crop_operation' && isEdit && formData.status === 'planned'" class="completion-section">
          <p class="completion-label">{{ t('operationObservationForm.completionLabelPrefix') }} <strong>{{ t('operationObservationForm.plannedLower') }}</strong> {{ t('operationObservationForm.completionLabelSuffix') }}</p>
          <div class="completion-actions">
            <AeButton variant="primary" size="small" @click="formData.status = 'done'">{{ t('operationObservationForm.markCompleted') }}</AeButton>
            <AeButton variant="danger" size="small" @click="formData.status = 'cancelled'">{{ t('operationObservationForm.cancelActivity') }}</AeButton>
          </div>
        </div>

        <!-- Badge stato finale (done / cancelled) in edit -->
        <div v-if="props.type === 'crop_operation' && isEdit && (formData.status === 'done' || formData.status === 'cancelled')" class="status-banner" :class="`status-${formData.status}`">
          <strong>{{ statusLabel(formData.status) }}</strong> — {{ t('operationObservationForm.statusWillUpdate') }}
          <button class="status-undo" @click="formData.status = 'planned'">{{ t('operationObservationForm.undoChange') }}</button>
        </div>

        <!-- Sezione Trappola (solo per id_survey_schema=1) -->
        <div v-if="isTrapObservation" class="trap-section">
          <label class="trap-label">{{ t('operationObservationForm.trap') }} *</label>
          <div class="trap-select-row">
            <AeButton variant="secondary" @click="showTrapUpsert = true" :title="t('operationObservationForm.newTrap')">
              {{ availableTraps.length === 0 ? t('operationObservationForm.addTrap') : '+' }}
            </AeButton>
            <select v-if="availableTraps.length > 0" v-model="formData.id_trap" class="form-control">
              <option value="" disabled>{{ t('operationObservationForm.selectTrapPlaceholder') }}</option>
              <option v-for="trap in availableTraps" :key="trap.id_trap" :value="trap.id_trap">
                {{ trap.name }}{{ trap.pest ? ` — ${trap.pest}` : '' }}
              </option>
            </select>
          </div>
        </div>

        <!-- Modal nuova trappola -->
        <AeModal v-model="showTrapUpsert" :title="t('operationObservationForm.newTrap')" size="large" flex-body>
          <TrapUpsert
            v-if="showTrapUpsert"
            ref="trapUpsertRef"
            :id-farm-center="idFarmCenter"
            :id-season="idSeason"
            :pre-selected-plot-ids="selectedPlotIds"
            @submit="handleTrapCreated"
            @cancel="showTrapUpsert = false"
          />
          <template #footer>
            <div class="modal-actions">
              <AeButton variant="secondary" @click="showTrapUpsert = false">{{ t('operationObservationForm.cancel') }}</AeButton>
              <AeButton variant="primary" :loading="trapUpsertRef?.saving" @click="trapUpsertRef?.save()">{{ t('operationObservationForm.save') }}</AeButton>
            </div>
          </template>
        </AeModal>

        <!-- Quantità acqua (solo trattamenti) -->
        <div v-if="isTreatment" class="ae-form-row">
          <label>{{ t('operationObservationForm.waterQuantity') }}</label>
          <input type="number" v-model.number="volumeHa" class="form-control" step="1" min="0" :placeholder="t('operationObservationForm.waterQuantityPlaceholder')" />
        </div>

        <!-- Sezione Prodotti (solo per operazioni che li richiedono) -->
        <div v-if="needsProducts" class="products-section">
          <div class="products-header">
            <h4>{{ t('operationObservationForm.products') }}</h4>
            <span v-if="totalAreaMq > 0" class="area-info">{{ t('operationObservationForm.totalArea') }}: {{ totalAreaMq.toFixed(0) }} m² ({{ totalAreaHa.toFixed(2) }} ha)</span>
          </div>

          <div class="products-list">
            <OperationProductRow
              v-for="(product, idx) in productRows"
              :key="idx"
              :index="idx"
              :products="filteredWarehouseProducts"
              :model-value="product"
              :crop-operation-type="selectedType.id"
              :total-area="totalAreaMq"
              :volume-ha="isTreatment ? (volumeHa || 0) : 0"
              :can-remove="productRows.length > 1"
              :crop-eppo-codes="selectedCropEppoCodes"
              :product-residuals="productResidualMap"
              :cu-accumulated="cuAccumulated"
              @update:model-value="updateProductRow(idx, $event)"
              @remove="removeProductRow(idx)"
              @create-product="openCreateProduct(idx)"
            />
          </div>

          <AeButton variant="secondary" size="small" @click="addProductRow" class="add-product-btn">
            + {{ t('operationObservationForm.addAnotherProduct') }}
          </AeButton>
        </div>

        <!-- Sezione Raccolta (solo per id_crop_operation=7) -->
        <div v-if="isRaccolta" class="raccolta-section">
          <h4 class="raccolta-title">{{ t('operationObservationForm.harvestQuantity') }}</h4>
          <div class="raccolta-grid">
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.unitOfMeasure') }}</label>
              <select v-model="raccoltaData.uom" class="form-control">
                <option value="kg">kg</option>
                <option value="q">{{ t('operationObservationForm.uomQuintals') }}</option>
                <option value="t">{{ t('operationObservationForm.uomTonnes') }}</option>
                <option value="g">{{ t('operationObservationForm.uomGrams') }}</option>
                <option value="l">{{ t('operationObservationForm.uomLiters') }}</option>
                <option value="ml">ml</option>
                <option value="n">n°</option>
              </select>
            </div>
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.totalQuantity') }} <span class="raccolta-uom-hint">{{ raccoltaData.uom || 'kg' }}</span></label>
              <input type="number" v-model.number="raccoltaData.amount_tot" @input="onAmountTotInput" class="form-control" step="0.01" min="0"   />
            </div>
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.quantityPerHectare') }} <span class="raccolta-uom-hint">{{ raccoltaData.uom || 'kg' }}/ha</span></label>
              <input type="number" v-model.number="raccoltaData.amount_ha" @input="onAmountHaInput" class="form-control" step="0.01" min="0"   />
            </div>
          </div>
          <p v-if="totalAreaHa > 0" class="raccolta-hint">{{ t('operationObservationForm.surfaceAutoCalcHint', { ha: totalAreaHa.toFixed(2) }) }}</p>
        </div>

        <!-- Sezione Concimazione organica (id_crop_operation=4) -->
        <div v-if="isOrganica" class="raccolta-section">
          <h4 class="raccolta-title">{{ t('operationObservationForm.quantity') }}</h4>
          <div class="raccolta-grid">
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.unitOfMeasure') }}</label>
              <select v-model="organicaData.uom" class="form-control">
                <option value="kg">kg</option>
                <option value="q">{{ t('operationObservationForm.uomQuintals') }}</option>
                <option value="t">{{ t('operationObservationForm.uomTonnes') }}</option>
                <option value="g">{{ t('operationObservationForm.uomGrams') }}</option>
                <option value="l">{{ t('operationObservationForm.uomLiters') }}</option>
                <option value="ml">ml</option>
                <option value="n">n°</option>
              </select>
            </div>
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.totalQuantity') }} <span class="raccolta-uom-hint">{{ organicaData.uom }}</span></label>
              <input type="number" v-model.number="organicaData.quantita_tot" @input="onOrganicaTotInput" class="form-control" step="0.01" min="0" />
            </div>
            <div class="ae-form-row">
              <label>{{ t('operationObservationForm.quantityPerHectare') }} <span class="raccolta-uom-hint">{{ organicaData.uom }}/ha</span></label>
              <input type="number" v-model.number="organicaData.quantita" @input="onOrganicaHaInput" class="form-control" step="0.01" min="0" />
            </div>
          </div>
          <p v-if="totalAreaHa > 0" class="raccolta-hint">{{ t('operationObservationForm.surfaceAutoCalcHint', { ha: totalAreaHa.toFixed(2) }) }}</p>
        </div>

        <!-- Sezione Mosca dell'olivo (id_survey_schema con custom='Mosca') -->
        <div v-if="isMoscaObservation" class="mosca-section">
          <h4>{{ t('operationObservationForm.olivFlyCountTitle') }}</h4>
          <MoscaCounter v-model="moscaData" />
        </div>

        <!-- Sezione Mosca completa (id_survey_schema = 10) -->
        <div v-if="isMoscaCompletaObservation" class="mosca-section">
          <h4>{{ t('operationObservationForm.olivFlyFullCountTitle') }}</h4>
          <MoscaCompletaCounter v-model="moscaCompletaData" />
        </div>

        <!-- Form dinamico dallo schema dbmng -->

        <!-- {{ dbmngSchemaWrapper }} -->

        <AeForm v-if="dbmngSchemaWrapper && !isMoscaObservation && !isMoscaCompletaObservation" ref="aeFormRef" :dbmngSchema="dbmngSchemaWrapper" v-model="dynamicFormData"
          :options="{ showActions: false, validateOnChange: false }" />

        <!-- Aggiungi un campo notes con le note-->
        <div class="field-group">
          <label>{{ t('operationObservationForm.notes') }}</label>
          <textarea v-model="formData.notes" class="form-control" rows="2" :placeholder="t('operationObservationForm.notesPlaceholder')"></textarea>
        </div>

        <ImageLoader
          :type="type === 'crop_operation' ? 'operation' : 'observation'"
          :record="draftId"
          v-model="images"
        />

        <!-- Modal creazione prodotto contestuale -->
        <AeModal v-model="showProductForm" :title="t('operationObservationForm.newProduct')" size="medium" :flexBody="true">
          <ProductForm
            v-if="showProductForm && productFormType"
            :farm-center-id="idFarmCenter"
            :product-type="productFormType"
            @submit="handleProductCreated"
            @cancel="showProductForm = false"
          />
        </AeModal>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { translateCropName } from '../i18n/cropVocab'
import { translateSchemaName } from '../i18n/schemaVocab'
import { useDbStore } from '../stores/dbStore.js'
import { useAppStore } from '../stores/app.js'
import { usePoderiStore } from '../stores/poderiStore.js'
import { AeCard, AeButton, AeForm, AeModal } from '@aevue/components'
import OperationProductRow from './OperationProductRow.vue'
import ProductForm from './ProductForm.vue'
import ImageLoader from './ImageLoader.vue'
import TrapUpsert from './TrapUpsert.vue'
import MoscaCounter from './MoscaCounter.vue'
import MoscaCompletaCounter from './MoscaCompletaCounter.vue'
import { getProductTypeForOperation, UOM_MULTIPLIER, calcResidual } from '../composables/useWarehouse'

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['crop_operation', 'observation'].includes(value)
  },
  idFarmCenter: {
    type: String,
    required: true
  },
  idSeason: {
    type: String,
    default: null
  },
  editObject: {
    type: Object,
    default: null
  },
  preSelectedType: {
    type: [String, Number],
    default: null
  },
  preSelectedDate: {
    type: String,
    default: null
  },
  preSelectedFields: {
    type: Array,
    default: () => []
  },
  preSelectedProductId: {
    type: String,
    default: null
  },
  preSelectedTrapId: {
    type: String,
    default: null
  },
  preSelectedNotes: {
    type: String,
    default: null
  },
  preSelectedProducts: {
    type: Array,
    default: () => []
  },
  preSelectedDynamic: {
    type: Object,
    default: () => ({})
  },
  extraJson: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['saved', 'cancel', 'step-change'])

const { t } = useI18n()

const dbStore = useDbStore()
const appStore = useAppStore()
const poderiStore = usePoderiStore()

// GUID pre-generato: in edit mode usa l'id esistente,
// in create mode ne genera uno subito per poter allegare immagini prima del salvataggio.
const draftId = (props.editObject?.id_operation || props.editObject?.id_observation) || crypto.randomUUID()

// --- Mosca dell'olivo ---
const moscaData = ref({ num_si: 0, num_no: 0 })
const isMoscaObservation = computed(() =>
  props.type === 'observation' && selectedType.value?.custom === 'Mosca'
)

// --- Mosca completa ---
const moscaCompletaData = ref({
  tot: 100,
  u: 0,
  l1v: 0,
  l2v: 0,
  l3v: 0,
  l1m: 0,
  l2m: 0,
  l3m: 0,
  pv: 0,
  pm: 0,
  fu: 0,
})
const isMoscaCompletaObservation = computed(() =>
  props.type === 'observation' && Number(selectedType.value?.id) === 10
)

// --- Trappolaggio ---
const TRAPPING_SCHEMA_ID = 1
const availableTraps = ref([])
const showTrapUpsert = ref(false)
const trapUpsertRef = ref(null)

const selectedPlotIds = computed(() => {
  const ids = new Set()
  for (const fieldId of formData.value.id_fields) {
    const field = fieldOptions.value.find(f => f.id_field === fieldId)
    if (field?.id_plot) ids.add(field.id_plot)
  }
  return [...ids]
})
const isTrapObservation = computed(() =>
  props.type === 'observation' && Number(selectedType.value?.id) === TRAPPING_SCHEMA_ID
)

// Codici EPPO delle colture dei campi selezionati (per PestTagger in OperationProductRow)
const selectedCropEppoCodes = computed(() => {
  const codes = new Set()
  for (const fieldId of formData.value.id_fields) {
    const field = fieldOptions.value.find(f => f.id_field === fieldId)
    const eppo = field?.crop?.eppo_code || field?.eppo_code
    if (eppo) codes.add(eppo.trim().toUpperCase())
  }
  return [...codes]
})

// State
const currentStep = ref('type')
const typeOptions = ref([])
const selectedType = ref(null)
const formSchema = ref(null)
const saving = ref(false)
const seasonName = ref('')

const formData = ref({
  date: new Date().toISOString().split('T')[0],
  id_field: '',
  id_fields: [],
  id_trap: null,
  status: null,
  surface_perc: null,
})

const surfacePercEnabled = ref(false)
const surfaceInputMode = ref('perc') // 'perc' | 'ha'
const surfaceHaInput = ref(null)

function onSurfacePercToggle() {
  if (!surfacePercEnabled.value) {
    formData.value.surface_perc = null
    surfaceHaInput.value = null
  } else {
    formData.value.surface_perc = 50
    surfaceInputMode.value = 'perc'
  }
}

function onSurfacePercInput(e) {
  formData.value.surface_perc = Math.min(99, Math.max(1, parseInt(e.target.value) || 50))
}

function onSurfaceHaInput(e) {
  const ha = parseFloat(e.target.value) || 0
  surfaceHaInput.value = ha
  const grossHa = totalAreaMqGross.value / 10000
  if (grossHa > 0 && ha > 0) {
    formData.value.surface_perc = Math.min(99, Math.max(1, Math.round((ha / grossHa) * 100)))
  }
}

function onSurfaceModeChange() {
  surfaceHaInput.value = null
}

watch(currentStep, (val) => emit('step-change', val))

// Raccolta (id_crop_operation=7) — ref separata, non passa per AeForm
const raccoltaData = ref({ amount_tot: null, amount_ha: null, uom: 'q', type_amount: null })

// Trattamento (id_crop_operation=10)
const isTreatment = computed(() => props.type === 'crop_operation' && String(selectedType.value?.id) === '10')
const volumeHa = ref(null)


const isFutureDate = computed(() => {
  if (!formData.value.date) return false
  return formData.value.date > new Date().toISOString().split('T')[0]
})

// Auto-set status in insert mode: futura → planned, passata → null
watch(() => formData.value.date, (newDate) => {
  if (isEdit.value) return
  if (props.type !== 'crop_operation') return
  const today = new Date().toISOString().split('T')[0]
  formData.value.status = newDate > today ? 'planned' : null
})

const STATUS_LABELS = computed(() => ({
  planned: t('operationObservationForm.statusPlanned'),
  done: t('operationObservationForm.statusDone'),
  cancelled: t('operationObservationForm.statusCancelled'),
}))
function statusLabel(s) { return STATUS_LABELS.value[s] || '' }

const dynamicFormData = ref({})
const aeFormRef = ref(null)
const images = ref([])
const productRows = ref([])
const showProductForm = ref(false)
const productFormType = ref(null)
const createProductForRowIndex = ref(null)
const fieldOptions = ref([])

// Set di id_crop dai campi attualmente selezionati (manualmente o via props)
const selectedFieldCropIds = computed(() => {
  const ids = new Set()
  const fieldIds = formData.value.id_fields.length > 0
    ? formData.value.id_fields
    : (props.preSelectedFields || [])
  for (const fieldId of fieldIds) {
    const field = fieldOptions.value.find(f => f.id_field === fieldId)
    const cropId = field?.crop?.id_crop || field?.id_crop
    if (cropId) ids.add(String(cropId))
  }
  return ids
})

// Tipi filtrati per le colture dei campi selezionati.
// - Nessun campo selezionato → tutti i tipi
// - Coltura unica → tipi di quella coltura + generici
// - Colture miste → solo tipi generici (evita di rimuovere campi al momento della scelta)
const filteredTypeOptions = computed(() => {
  if (selectedFieldCropIds.value.size === 0) return typeOptions.value
  if (selectedFieldCropIds.value.size > 1) {
    return typeOptions.value.filter(opt => !opt.id_crop || String(opt.id_crop) === '0')
  }
  const [singleCropId] = selectedFieldCropIds.value
  return typeOptions.value.filter(opt =>
    !opt.id_crop || String(opt.id_crop) === '0' || String(opt.id_crop) === singleCropId
  )
})

// Campi filtrati per id_crop del tipo selezionato (mostra tutti se tipo non ha coltura specifica)
const filteredFieldOptions = computed(() => {
  if (!selectedType.value?.id_crop || String(selectedType.value.id_crop) === '0') {
    return fieldOptions.value
  }
  return fieldOptions.value.filter(f => {
    const cropId = f.crop?.id_crop || f.id_crop
    return cropId && String(cropId) === String(selectedType.value.id_crop)
  })
})

// Colture disponibili per i filtri rapidi
const availableCrops = computed(() => {
  const crops = new Set()
  filteredFieldOptions.value.forEach(f => {
    const name = f.crop?.name || f.crop?.crop_name
    if (name) crops.add(name)
  })
  return [...crops].sort()
})

function isCropFullySelected(cropName) {
  const cropFieldIds = filteredFieldOptions.value
    .filter(f => (f.crop?.name || f.crop?.crop_name) === cropName)
    .map(f => f.id_field)
  return cropFieldIds.length > 0 && cropFieldIds.every(id => formData.value.id_fields.includes(id))
}

function toggleCropFields(cropName) {
  const cropFieldIds = filteredFieldOptions.value
    .filter(f => (f.crop?.name || f.crop?.crop_name) === cropName)
    .map(f => f.id_field)

  if (isCropFullySelected(cropName)) {
    formData.value.id_fields = formData.value.id_fields.filter(id => !cropFieldIds.includes(id))
  } else {
    const current = new Set(formData.value.id_fields)
    cropFieldIds.forEach(id => current.add(id))
    formData.value.id_fields = [...current]
  }
}

function toggleAllFields() {
  if (formData.value.id_fields.length === filteredFieldOptions.value.length) {
    formData.value.id_fields = []
  } else {
    formData.value.id_fields = filteredFieldOptions.value.map(f => f.id_field)
  }
}

function toggleField(fieldId) {
  const idx = formData.value.id_fields.indexOf(fieldId)
  if (idx >= 0) {
    formData.value.id_fields.splice(idx, 1)
  } else {
    formData.value.id_fields.push(fieldId)
  }
}

function formatArea(value) {
  if (!value && value !== 0) return '-'
  const ha = parseFloat(value) / 10000
  return ha.toFixed(2)
}

const selectedArea = computed(() => {
  let total = 0
  for (const fieldId of formData.value.id_fields) {
    const field = fieldOptions.value.find(f => f.id_field === fieldId)
    const area = field?.plot?.area || field?.plot?.surface
    if (area) total += parseFloat(area) || 0
  }
  return total
})

// Computed
const VALID_TYPES = ['crop_operation', 'observation']
const isValidType = computed(() => VALID_TYPES.includes(props.type))
const isEdit = computed(() => !!props.editObject)
const typeLabel = computed(() => props.type === 'crop_operation' ? t('operationObservationForm.typeLabelOperation') : t('operationObservationForm.typeLabelObservation'))
const tableName = computed(() => props.type === 'crop_operation' ? 'operation' : 'observation')

const selectedFieldNames = computed(() => {
  let ids = formData.value.id_fields
  if (!Array.isArray(ids)) {
    const raw = formData.value.id_field
    if (typeof raw === 'string' && raw.startsWith('[')) {
      try { ids = JSON.parse(raw) } catch { ids = [] }
    } else {
      ids = raw ? [raw] : []
    }
  }
  if (ids.length === 0) return ''
  const names = ids.map(id => {
    const field = fieldOptions.value.find(f => f.id_field === id)
    return field?.name || t('operationObservationForm.notAvailable')
  })
  return names.join(', ')
})

const dbmngSchemaWrapper = computed(() => {
  if (!formSchema.value) return null


  // //nascondi i campi
  // jQuery(`.dbmng_form_field_k_${order}, .dbmng_form_field_p_${order}, .dbmng_form_field_n1_${order}, .dbmng_form_field_n2_${order}, .dbmng_form_field_n3_${order}`).hide();
  // //vecchi campi (poi si tolgono dal dbmng)
  // jQuery(`.dbmng_form_field_k2o, .dbmng_form_field_p2o5, .dbmng_form_field_no3, .dbmng_form_field_nh4, .dbmng_form_field_urea`).hide();
  // jQuery(`.dbmng_form_field_dose_ha, .dbmng_form_field_nome_prodotto,  .dbmng_form_field_target,  .dbmng_form_field_udm `).hide();

  //Attenzione; dovrebbe essere un array associativo.
  //se showProducts è true, allora elimino dall'array i campi dose_ha, nome_prodotto, target, udm (che sono quelli che uso per la gestione prodotti custom)
  if (needsProducts.value) {
    const fieldsToHide = ['dose_ha', 'nome_prodotto', 'target', 'udm', 'k2o', 'p2o5', 'no3', 'nh4', 'urea', 'volume_ha'];

    if (String(selectedType.value?.id) === '9') {
      fieldsToHide.push('uom', 'amount_tot', 'amount_ha', 'type_amount','cultivar')
    }

    for (const fieldName of fieldsToHide) {
      if (formSchema.value[fieldName]) {
        delete formSchema.value[fieldName]
      }
    }
  }

  if (isRaccolta.value) {
    for (const fieldName of ['amount_tot', 'amount_ha', 'type_amount', 'uom']) {
      if (formSchema.value[fieldName]) {
        delete formSchema.value[fieldName]
      }
    }
  }

  if (isOrganica.value) {
    if (formSchema.value['quantita']) delete formSchema.value['quantita']
  }

  return { fields: formSchema.value }
})

// Verifica se il tipo operazione selezionato richiede gestione prodotti
const needsProducts = computed(() => {
  if (props.type !== 'crop_operation' || !selectedType.value) return false
  return !!getProductTypeForOperation(selectedType.value.id)
})

// Prodotti dal magazzino filtrati per il tipo di operazione
const today = new Date().toISOString().substring(0, 10)

const filteredWarehouseProducts = computed(() => {
  if (!selectedType.value) return []
  const pt = getProductTypeForOperation(selectedType.value.id)
  if (!pt) return []
  return poderiStore.products.filter(p =>
    p.id_product_type === pt.id_product_type &&
    (!p.date_end || p.date_end > today)
  )
})

// Area totale lorda in m² dei campi selezionati (plot.area è in m² nel DB)
const totalAreaMqGross = computed(() => {
  if (!formData.value.id_fields || formData.value.id_fields.length === 0) return 0
  let total = 0
  for (const fieldId of formData.value.id_fields) {
    const field = fieldOptions.value.find(f => f.id_field === fieldId)
    if (field?.plot?.area) {
      total += parseFloat(field.plot.area) || 0
    }
  }
  return total
})

// Area effettiva considerando la riduzione di superficie (surface_perc in val)
const surfacePerc = computed(() => formData.value.surface_perc ?? 100)
const totalAreaMq = computed(() => totalAreaMqGross.value * (surfacePerc.value / 100))

// Area in ha per visualizzazione
const totalAreaHa = computed(() => totalAreaMq.value / 10000)

// Cu/ha già applicato sui campi selezionati in stagione (esclude l'operazione in modifica)
const cuAccumulated = computed(() => {
  if (props.type !== 'crop_operation') return 0
  const productMap = new Map(poderiStore.products.map(p => [p.id_product, p]))
  const fieldCu = new Map()

  poderiStore.operations
    .filter(op => {
      if (op.id_crop_operation !== 10) return false
      if (props.idSeason && op.id_season !== props.idSeason) return false
      if (isEdit.value && op.id_operation === props.editObject?.id_operation) return false
      return true
    })
    .forEach(op => {
      const opFieldIds = Array.isArray(op.id_fields) ? op.id_fields
        : op.id_field ? [op.id_field] : []
      ;(Array.isArray(op.products) ? op.products : []).forEach(p => {
        const cuPerc = parseFloat(productMap.get(p.id_product)?.cu_perc) || 0
        if (cuPerc <= 0) return
        const cuKgHa = (parseFloat(p.amount_ha) || 0) * (UOM_MULTIPLIER[p.uom] || 1) * cuPerc / 100
        opFieldIds.forEach(fid => fieldCu.set(fid, (fieldCu.get(fid) || 0) + cuKgHa))
      })
    })

  const selected = formData.value.id_fields
  if (!selected || selected.length === 0) return 0
  return Math.max(0, ...selected.map(fid => fieldCu.get(fid) || 0))
})

// Mappa residui magazzino per i prodotti disponibili (esclude operazione in modifica)
const productResidualMap = computed(() => {
  const excludeId = isEdit.value ? props.editObject?.id_operation : null
  const ops = excludeId
    ? poderiStore.operations.filter(op => op.id_operation !== excludeId)
    : poderiStore.operations
  const map = {}
  for (const product of filteredWarehouseProducts.value) {
    map[product.id_product] = calcResidual(product, ops)
  }
  return map
})

// Raccolta (id_crop_operation=7)
const isRaccolta = computed(() =>
  props.type === 'crop_operation' && String(selectedType.value?.id) === '7'
)

// Concimazione organica (id_crop_operation=4)
const isOrganica = computed(() =>
  props.type === 'crop_operation' && String(selectedType.value?.id) === '4'
)
const organicaData = ref({ quantita: null, quantita_tot: null, uom: 'kg' })

function onOrganicaHaInput() {
  if (totalAreaHa.value > 0 && organicaData.value.quantita != null && organicaData.value.quantita !== '') {
    organicaData.value.quantita_tot = parseFloat((organicaData.value.quantita * totalAreaHa.value).toFixed(2))
  }
}

function onOrganicaTotInput() {
  if (totalAreaHa.value > 0 && organicaData.value.quantita_tot != null && organicaData.value.quantita_tot !== '') {
    organicaData.value.quantita = parseFloat((organicaData.value.quantita_tot / totalAreaHa.value).toFixed(2))
  }
}

function onAmountHaInput() {
  raccoltaData.value.type_amount = 'ha'
  if (totalAreaHa.value > 0 && raccoltaData.value.amount_ha != null && raccoltaData.value.amount_ha !== '') {
    raccoltaData.value.amount_tot = parseFloat((raccoltaData.value.amount_ha * totalAreaHa.value).toFixed(4))
  }
}

function onAmountTotInput() {
  raccoltaData.value.type_amount = 'tot'
  if (totalAreaHa.value > 0 && raccoltaData.value.amount_tot != null && raccoltaData.value.amount_tot !== '') {
    raccoltaData.value.amount_ha = parseFloat((raccoltaData.value.amount_tot / totalAreaHa.value).toFixed(4))
  }
}

// Methods
async function loadTraps() {
  const result = await dbStore.select('trap', {
    id_farm_center: props.idFarmCenter,
    ...(props.idSeason ? { id_season: props.idSeason } : {})
  })
  availableTraps.value = (result?.data || []).filter(trap => trap.automatic !== true)
}

async function handleTrapCreated(newId) {
  showTrapUpsert.value = false
  await loadTraps()
  formData.value.id_trap = newId
}

const loadMetadata = async () => {
  try {
    const promises = []

    if (props.type === 'crop_operation') {
      promises.push(
        dbStore.select('crop_operation', { active: 1 }).then(result => {
          const operations = (result?.data || [])
            .slice()
            .sort((a, b) => (a.order_n ?? 999999) - (b.order_n ?? 999999))
          typeOptions.value = operations.map(op => ({
            id: op.id_crop_operation,
            id_crop: op.id_crop || null,
            name: op.name || t('operationObservationForm.operationFallbackName', { id: op.id_crop_operation }),
            description: op.description,
            schema: op.schema
          }))
        })
      )
    } else {
      promises.push(
        dbStore.select('survey_schema', { active: 1 }).then(result => {
          const schemas = (result?.data || [])
            .slice()
            .sort((a, b) => (a.order_n ?? 999999) - (b.order_n ?? 999999))
          typeOptions.value = schemas.map(schema => ({
            id: schema.id_survey_schema,
            id_crop: schema.id_crop || null,
            name: schema.name || t('operationObservationForm.observationFallbackName', { id: schema.id_survey_schema }),
            description: schema.description,
            schema: schema.schema,
            custom: schema.custom || null,
          }))
        })
      )
    }

    // Carica stagione, fields e prodotti in parallelo
    if (props.idSeason) {
      promises.push(
        dbStore.selectOne('season', props.idSeason).then(result => {
          if (result?.ok && result?.data) {
            const season = result.data
            seasonName.value = season.name || season.season_name || `${season.year || ''}`
          }
        })
      )
    }

    promises.push(poderiStore.fetchFieldsByFarmCenter(props.idFarmCenter, props.idSeason))
    promises.push(poderiStore.fetchProductsByFarmCenter(props.idFarmCenter))

    if (props.type === 'observation') {
      promises.push(loadTraps())
    }

    await Promise.all(promises)
  } catch (error) {
    console.error('Errore nel caricamento dei metadata:', error)
  }
}

const selectType = (type) => {
  selectedType.value = type
  formSchema.value = type.schema

  // Inizializza la sezione prodotti se necessario
  if (getProductTypeForOperation(type.id) && productRows.value.length === 0) {
    productRows.value = [createEmptyProductRow()]
  }

  // Se il tipo è crop-specifico, rimuovi dalla selezione i campi con coltura diversa
  if (type.id_crop && String(type.id_crop) !== '0') {
    formData.value.id_fields = formData.value.id_fields.filter(fieldId => {
      const field = fieldOptions.value.find(f => f.id_field === fieldId)
      const cropId = field?.crop?.id_crop || field?.id_crop
      return cropId && String(cropId) === String(type.id_crop)
    })
  }

  // Se i campi sono già pre-selezionati (props) → usa quelli e vai al form
  if (props.preSelectedFields && props.preSelectedFields.length > 0 && !isEdit.value) {
    formData.value.id_fields = [...props.preSelectedFields]
    formData.value.id_field = props.preSelectedFields[0]
    currentStep.value = 'form'
  // Se i campi sono stati scelti manualmente nello step 'dateField' → vai al form
  } else if (formData.value.id_fields.length > 0 && !isEdit.value) {
    currentStep.value = 'form'
  } else {
    currentStep.value = 'dateField'
  }
}

function createEmptyProductRow() {
  return {
    id_product: '',
    product_name: '',
    uom: 'kg',
    amount_tot: null,
    amount_ha: null,
    dose_n: 0,
    dose_p: 0,
    dose_k: 0,
    pests: [],
  }
}

function addProductRow() {
  productRows.value.push(createEmptyProductRow())
}

function removeProductRow(index) {
  if (productRows.value.length > 1) {
    productRows.value.splice(index, 1)
  }
}

function updateProductRow(index, data) {
  productRows.value[index] = { ...data }
}

function openCreateProduct(rowIndex) {
  if (!selectedType.value) return
  const pt = getProductTypeForOperation(selectedType.value.id)
  if (!pt) return
  productFormType.value = pt.id_product_type
  createProductForRowIndex.value = rowIndex
  showProductForm.value = true
}

async function handleProductCreated(productId) {
  showProductForm.value = false
  // Ricarica prodotti dal magazzino
  await poderiStore.fetchProductsByFarmCenter(props.idFarmCenter)
  // Trova il prodotto appena creato
  const newProduct = poderiStore.products.find(p => p.id_product === productId)
  if (newProduct && createProductForRowIndex.value != null) {
    const idx = createProductForRowIndex.value
    const row = productRows.value[idx] || createEmptyProductRow()
    row.id_product = newProduct.id_product
    row.product_name = newProduct.name || ''
    // Auto-compila dosi NPK per fertilizzanti
    if (parseInt(selectedType.value.id) === 3) {
      row.dose_n = parseFloat(newProduct.n) || 0
      row.dose_p = parseFloat(newProduct.p) || 0
      row.dose_k = parseFloat(newProduct.k) || 0
    }
    productRows.value[idx] = { ...row }
  }
  createProductForRowIndex.value = null
}

const goToForm = () => {
  if (!formData.value.id_fields || formData.value.id_fields.length === 0) {
    alert(t('operationObservationForm.selectAtLeastOneField'))
    return
  }
  // Flusso field-first: tipo non ancora scelto → vai allo step tipo
  if (!selectedType.value) {
    currentStep.value = 'type'
  } else {
    currentStep.value = 'form'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('it-IT')
}

const handleCancel = () => {
  emit('cancel')
}

const handleSubmit = async () => {
  if (!formData.value.date) {
    alert(t('operationObservationForm.dateRequired'))
    return
  }

  // Valida il form dinamico prima di salvare
  if (dbmngSchemaWrapper.value) {
    const validation = aeFormRef.value?.validate()
    if (validation && !validation.valid) return
  }

  try {
    saving.value = true

    const firstField = formData.value.id_fields && formData.value.id_fields.length > 0
      ? formData.value.id_fields[0]
      : ''

    const dataToSave = {
      id_farm_center: props.idFarmCenter,
      id_season: props.idSeason,
      id_field: firstField,
      id_fields: formData.value.id_fields,
      date: formData.value.date,
      notes: formData.value.notes || '',
      images: images.value,
      ...dynamicFormData.value,
      ...(isRaccolta.value ? raccoltaData.value : {})
    }

    if (formData.value.surface_perc != null) {
      dataToSave.surface_perc = formData.value.surface_perc
    }

    if (isTreatment.value && volumeHa.value != null) {
      dataToSave.volume_ha = volumeHa.value
    }

    if (props.type === 'crop_operation') {
      dataToSave.status = formData.value.status || null
      dataToSave.id_crop_operation = selectedType.value.id

      // Aggiungi prodotti se il tipo operazione li richiede
      if (needsProducts.value && productRows.value.length > 0) {
        const products = productRows.value
          .filter(p => p.id_product || p.product_name)
          .map(p => ({
            id_product: p.id_product === 'xxx' ? null : p.id_product,
            product_name: p.product_name,
            uom: p.uom,
            amount_tot: p.amount_tot,
            amount_ha: p.amount_ha,
            ...(parseInt(selectedType.value.id) === 3 ? {
              dose_n: p.dose_n,
                // dose_nh4: p.dose_nh4,
                // dose_urea: p.dose_urea,
              dose_p: p.dose_p,
              dose_k: p.dose_k,
              // Effluenti di allevamento: coefficienti di disponibilità N pluriennale
              org_type: p.org_type ?? null,
              org_nitrogen_conc: p.org_nitrogen_conc ?? null,
              org_nitrogen_available: p.org_nitrogen_available ?? null,
              n_min_first_year: p.n_min_first_year ?? null,
              org_nitrogen_available_first_year: p.org_nitrogen_available_first_year ?? null
            } : {}),
            ...(parseInt(selectedType.value.id) === 10 ? {
              pests: Array.isArray(p.pests) ? p.pests : [],
              // Verifica conformità BDF (ParlaConVerificaQdca): conservata, diventa "da riverificare" se cambiano i dati
              verification: p.verification ?? null
            } : {})
          }))

        dataToSave.products = products

        // Per concimazione organica: salva quantita e quantita_tot
        if (parseInt(selectedType.value.id) === 4) {
          dataToSave.quantita     = organicaData.value.quantita
          dataToSave.quantita_tot = organicaData.value.quantita_tot
          dataToSave.uom          = organicaData.value.uom
        }

        // Per concimazione: calcola totali NPK
        if (parseInt(selectedType.value.id) === 3) {
          let totalN = 0, totalP = 0, totalK = 0
          for (const p of products) {
            const mult = UOM_MULTIPLIER[p.uom] || 1
            const amountHa = parseFloat(p.amount_ha) || 0
            totalN += mult * amountHa * ((parseFloat(p.dose_n) || 0) ) / 100 //+ (parseFloat(p.dose_nh4) || 0) + (parseFloat(p.dose_urea) || 0)
            totalP += mult * amountHa * (parseFloat(p.dose_p) || 0) / 100
            totalK += mult * amountHa * (parseFloat(p.dose_k) || 0) / 100
          }
          dataToSave.no3 = parseFloat(totalN.toFixed(2))
          dataToSave.p2o5 = parseFloat(totalP.toFixed(2))
          dataToSave.k2o = parseFloat(totalK.toFixed(2))
        }
      }
    } else {
      dataToSave.id_survey_schema = selectedType.value.id

      // Mosca: aggiungi contatori al payload
      if (isMoscaObservation.value) {
        dataToSave.num_si = moscaData.value.num_si
        dataToSave.num_no = moscaData.value.num_no
      }

      // Mosca completa: aggiungi tutti i contatori
      if (isMoscaCompletaObservation.value) {
        dataToSave.tot = moscaCompletaData.value.tot
        dataToSave.u = moscaCompletaData.value.u
        dataToSave.l1v = moscaCompletaData.value.l1v
        dataToSave.l2v = moscaCompletaData.value.l2v
        dataToSave.l3v = moscaCompletaData.value.l3v
        dataToSave.l1m = moscaCompletaData.value.l1m
        dataToSave.l2m = moscaCompletaData.value.l2m
        dataToSave.l3m = moscaCompletaData.value.l3m
        dataToSave.pv = moscaCompletaData.value.pv
        dataToSave.pm = moscaCompletaData.value.pm
        dataToSave.fu = moscaCompletaData.value.fu
      }

      // Trappolaggio: verifica selezione trappola
      if (isTrapObservation.value) {
        if (!formData.value.id_trap) {
          alert(t('operationObservationForm.selectOrCreateTrap'))
          saving.value = false
          return
        }
        dataToSave.id_trap = formData.value.id_trap
      }
    }

    // Riferimento al messaggio vocale di origine (solo in creazione, vedi ParlaCon.vue)
    // Campo extra non in metadata.py: il backend lo instrada da solo nella colonna json
    if (!isEdit.value && props.extraJson) {
      Object.assign(dataToSave, props.extraJson)
    }

    let result
    if (isEdit.value) {
      const idField = props.type === 'crop_operation' ? 'id_operation' : 'id_observation'
      result = await dbStore.update(
        tableName.value,
        { [idField]: props.editObject[idField] },
        dataToSave
      )
    } else {
      const pkField = props.type === 'crop_operation' ? 'id_operation' : 'id_observation'
      dataToSave[pkField] = draftId
      result = await dbStore.insert(tableName.value, dataToSave)
    }

    console.log('Operation saved:', result)
    emit('saved', result)
  } catch (error) {
    console.error('Errore nel salvataggio:', error)
    alert(t('operationObservationForm.saveError', { error: error.message }))
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(async () => {
  if (!isValidType.value) {
    console.error(`OperationObservationForm: tipo "${props.type}" non valido. Tipi validi: ${VALID_TYPES.join(', ')}`)
    return
  }

  // Carica i campi per il farm center e la stagione
  const fields = await poderiStore.fetchFieldsByFarmCenter(props.idFarmCenter, props.idSeason)

  fieldOptions.value = fields || []

  await loadMetadata()

  // Pre-selezione prodotto dal magazzino
  if (props.preSelectedProductId && !isEdit.value) {
    const product = poderiStore.products.find(p => p.id_product === props.preSelectedProductId)
    if (product) {
      // Trova il tipo operazione associato al tipo prodotto
      const PRODUCT_OPERATION_MAP = [
        { id_product_type: 'ferti', id_crop_operation: 3 },
        { id_product_type: 'pesti', id_crop_operation: 10 },
        { id_product_type: 'seed', id_crop_operation: 9 }
      ]
      const matchingPt = PRODUCT_OPERATION_MAP.find(m => m.id_product_type === product.id_product_type) || null

      if (matchingPt) {
        const opType = typeOptions.value.find(opt =>
          opt.id === matchingPt.id_crop_operation ||
          opt.id == matchingPt.id_crop_operation ||
          String(opt.id) === String(matchingPt.id_crop_operation)
        )
        if (opType) {
          selectType(opType)
          // Pre-compila il primo prodotto
          productRows.value = [{
            ...createEmptyProductRow(),
            id_product: product.id_product,
            product_name: product.name,
            ...(matchingPt.id_crop_operation === 3 ? {
              dose_n: parseFloat(product.n) || 0,
              dose_p: parseFloat(product.p) || 0,
              dose_k: parseFloat(product.k) || 0
            } : {})
          }]
        }
      }
    }
  }
  // Pre-selezione tipo operazione (dal GanttPlanner)
  else if (props.preSelectedType && !isEdit.value) {
    const type = typeOptions.value.find(opt =>
      opt.id === props.preSelectedType ||
      opt.id == props.preSelectedType ||
      String(opt.id) === String(props.preSelectedType)
    )

    if (type) {
      selectType(type)

      if (props.preSelectedDate) {
        formData.value.date = props.preSelectedDate
      }
      if (props.preSelectedFields && props.preSelectedFields.length > 0) {
        formData.value.id_fields = props.preSelectedFields
        formData.value.id_field = props.preSelectedFields[0]
        currentStep.value = 'form'
      } else {
        currentStep.value = 'dateField'
      }

      // Pre-compila prodotti da voce (solo se l'operazione li richiede)
      if (props.preSelectedProducts?.length && needsProducts.value) {
        productRows.value = props.preSelectedProducts.map(p => ({
          ...createEmptyProductRow(),
          id_product: p.id_product || 'xxx',
          product_name: p.product_name_raw || p.product_name || '',
          uom: p.uom || 'kg',
          amount_tot: p.amount ?? null,
          amount_ha: p.amount_ha ?? null,
          pests: Array.isArray(p.pests) ? p.pests : [],
        }))
      }
    } else {
      console.error('Tipo non trovato. Cercato:', props.preSelectedType, 'Tipo:', typeof props.preSelectedType)
      console.error('Disponibili:', typeOptions.value.map(opt => ({ id: opt.id, tipo: typeof opt.id, name: opt.name })))
    }
  }

  // Pre-selezione trappola (dal pannello dettaglio trappola)
  if (props.preSelectedTrapId && !isEdit.value) {
    formData.value.id_trap = props.preSelectedTrapId
  }

  // Pre-compila note da voce
  if (!isEdit.value && props.preSelectedNotes) {
    formData.value.notes = props.preSelectedNotes
  }

  // Pre-compila campi dinamici (es. water per irrigazione)
  if (!isEdit.value && props.preSelectedDynamic && Object.keys(props.preSelectedDynamic).length > 0) {
    const { volume_ha: dynVolumeHa, surface_perc: dynSurfacePerc, ...restDynamic } = props.preSelectedDynamic
    dynamicFormData.value = { ...dynamicFormData.value, ...restDynamic }
    if (dynVolumeHa != null) volumeHa.value = dynVolumeHa
    if (dynSurfacePerc != null) {
      formData.value.surface_perc = dynSurfacePerc
      surfacePercEnabled.value = true
    }
  }

  // Flusso field-first: nessuna pre-selezione e non edit → parti dalla selezione campi
  if (!isEdit.value
      && !props.preSelectedType
      && !props.preSelectedProductId
      && (!props.preSelectedFields || props.preSelectedFields.length === 0)) {
    currentStep.value = 'dateField'
  }

  // Modalità edit
  if (isEdit.value) {
    let fields = props.editObject.id_fields
    if (typeof fields === 'string') {
      try { fields = JSON.parse(fields) } catch { fields = [] }
    }
    if (!Array.isArray(fields) || fields.length === 0) {
      fields = props.editObject.id_field ? [props.editObject.id_field] : []
    }

    formData.value = {
      date: props.editObject.date || formData.value.date,
      id_field: props.editObject.id_field || '',
      id_fields: fields,
      notes: props.editObject.notes || '',
      status: props.editObject.status || null
    }
    images.value = Array.isArray(props.editObject.images) ? props.editObject.images : []

    // Estrai dati dinamici
    if (props.editObject.id_trap) {
      formData.value.id_trap = props.editObject.id_trap
    }
    if (props.editObject.num_si != null || props.editObject.num_no != null) {
      moscaData.value = {
        num_si: Number(props.editObject.num_si) || 0,
        num_no: Number(props.editObject.num_no) || 0,
      }
    }
    if (props.editObject.tot != null || props.editObject.u != null) {
      moscaCompletaData.value = {
        tot: Number(props.editObject.tot) || 0,
        u: Number(props.editObject.u) || 0,
        l1v: Number(props.editObject.l1v) || 0,
        l2v: Number(props.editObject.l2v) || 0,
        l3v: Number(props.editObject.l3v) || 0,
        l1m: Number(props.editObject.l1m) || 0,
        l2m: Number(props.editObject.l2m) || 0,
        l3m: Number(props.editObject.l3m) || 0,
        pv: Number(props.editObject.pv) || 0,
        pm: Number(props.editObject.pm) || 0,
        fu: Number(props.editObject.fu) || 0,
      }
    }

    const baseFields = ['id_operation', 'id_observation', 'id_farm_center', 'id_season',
                        'id_field', 'id_fields', 'id_trap', 'date', 'id_crop_operation', 'id_survey_schema',
                        'created_at', 'updated_at', 'custom', 'field_names', 'operation_type_name',
                        'observation_type_name', '_schema', 'products', 'no3', 'p2o5', 'k2o', 'status', 'pests',
                        'num_si', 'num_no']
    const dynamicData = {}
    Object.keys(props.editObject).forEach(key => {
      if (!baseFields.includes(key)) {
        dynamicData[key] = props.editObject[key]
      }
    })
    dynamicFormData.value = dynamicData

    // Trova e seleziona il tipo
    const typeId = props.type === 'crop_operation'
      ? props.editObject.id_crop_operation
      : props.editObject.id_survey_schema

    const type = typeOptions.value.find(opt => opt.id === typeId)
    if (type) {
      selectType(type)
      currentStep.value = 'form'

      // Ripristina prodotti se presenti
      if (props.editObject.products && Array.isArray(props.editObject.products) && props.editObject.products.length > 0) {
        productRows.value = props.editObject.products.map(p => ({
          id_product: p.id_product || 'xxx',
          product_name: p.product_name || '',
          uom: p.uom || 'kg',
          amount_tot: p.amount_tot,
          amount_ha: p.amount_ha,
          dose_n: p.dose_n || 0,
          dose_p: p.dose_p || 0,
          dose_k: p.dose_k || 0,
          pests: Array.isArray(p.pests) ? p.pests : [],
          verification: p.verification ?? null,
          org_type: p.org_type ?? null,
          org_nitrogen_conc: p.org_nitrogen_conc ?? null,
          org_nitrogen_available: p.org_nitrogen_available ?? null,
          n_min_first_year: p.n_min_first_year ?? null,
          org_nitrogen_available_first_year: p.org_nitrogen_available_first_year ?? null,
        }))
      }

      // Ripristina campi raccolta se raccolta (id=7)
      if (String(type.id) === '7') {
        raccoltaData.value = {
          amount_tot: props.editObject.amount_tot ?? null,
          amount_ha:  props.editObject.amount_ha  ?? null,
          uom:        props.editObject.uom        || 'q',
          type_amount: props.editObject.type_amount || null,
        }
      }

      // Ripristina campi concimazione organica se id=4
      if (String(type.id) === '4') {
        organicaData.value = {
          quantita:     props.editObject.quantita     ?? null,
          quantita_tot: props.editObject.quantita_tot ?? null,
          uom:          props.editObject.uom          || 'kg',
        }
      }
    }

    // Ripristina surface_perc
    if (props.editObject.surface_perc != null) {
      formData.value.surface_perc = props.editObject.surface_perc
      surfacePercEnabled.value = true
    }

    // Ripristina volume_ha
    if (props.editObject.volume_ha != null) {
      volumeHa.value = props.editObject.volume_ha
    }
  }
})

// Esponi metodi per il parent
defineExpose({
  currentStep,
  formData,
  goToForm,
  handleCancel,
  handleSubmit
})
</script>

<style scoped>
.operation-observation-form {
  padding: 1rem;
}

.step-container {
  max-width: 800px;
  margin: 0 auto;
}

.step-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.surface-perc-row {
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.surface-perc-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4a5568;
  cursor: pointer;
}
.surface-perc-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.6rem;
}
.surface-mode-select {
  width: 5rem;
}
.surface-perc-field {
  width: 6rem;
}
.surface-perc-hint {
  font-size: 0.8rem;
  color: #718096;
}
.area-reduced {
  font-size: 0.8rem;
  color: #718096;
  font-weight: normal;
}

h3 {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.step-header-row h3 {
  margin-bottom: 0;
}

.type-info {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  background: #e8f5e9;
  border-left: 3px solid #4caf50;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #1b5e20;
}

.type-info strong {
  color: #2e7d32;
}

.type-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.type-card:hover {
  transform: translateY(-2px);
}

.type-card h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.type-card p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
}

.form-section {
  background: var(--card-bg, #fff);
  padding: 1.5rem;
  border-radius: var(--border-radius, 8px);
  border: 1px solid var(--border-color, #ddd);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-color, #333);
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
  font-size: 1rem;
  font-family: inherit;
}

/* Raccolta */
.raccolta-section {
  padding: 1rem;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
  background: #f0fdf4;
  margin-bottom: 0.5rem;
}

.raccolta-title {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #166534;
}

.raccolta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.75rem;
}

@media (max-width: 640px) {
  .raccolta-grid { grid-template-columns: 1fr; }
}

.raccolta-uom-hint {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
}

.raccolta-hint {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #166534;
}

.ae-form-row {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.ae-form-row label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary, #2d3748);
}

.form-control:focus {
  outline: none;
  border-color: var(--button-bg, #4299e1);
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.form-control[multiple] {
  min-height: 150px;
}

.helper-text {
  margin: 0.5rem 0 0 0;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

.form-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-info {
  flex: 1;
  margin: 0;
  padding: 0.75rem;
  background: var(--color-info-bg, #e6f7ff);
  border-left: 4px solid var(--color-info, #2196f3);
  border-radius: 4px;
  font-size: 0.95rem;
}

.season-info {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  background: var(--color-info-bg, #e6f7ff);
  border-left: 3px solid var(--color-info, #2196f3);
  border-radius: 4px;
  font-size: 0.9rem;
  color: var(--text-color, #333);
}

.season-info strong {
  color: var(--color-info, #2196f3);
}

.future-date-warning {
  margin: 0.5rem 0 1rem;
  padding: 0.5rem 0.75rem;
  background: #fff8e1;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.88rem;
  color: #92400e;
}

.status-banner {
  margin: 0.5rem 0 1rem;
  padding: 0.55rem 0.85rem;
  border-radius: 6px;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.status-planned  { background: #f3e8ff; border-left: 3px solid #7c3aed; color: #4c1d95; }
.status-done     { background: #dcfce7; border-left: 3px solid #16a34a; color: #14532d; }
.status-cancelled{ background: #fee2e2; border-left: 3px solid #dc2626; color: #7f1d1d; }

.status-undo {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  text-decoration: underline;
  color: inherit;
  opacity: 0.7;
  padding: 0;
}

.completion-section {
  margin: 0.5rem 0 1rem;
  padding: 0.75rem;
  background: #f3e8ff;
  border-left: 3px solid #7c3aed;
  border-radius: 6px;
}

.completion-label {
  margin: 0 0 0.6rem;
  font-size: 0.88rem;
  color: #4c1d95;
}

.completion-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.original-date-info {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  background: #fff3cd;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #856404;
}

.original-date-info strong {
  color: #d39e00;
}

/* Products section */
.products-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.products-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.products-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.area-info {
  font-size: 0.8rem;
  color: #4a5568;
  background: #edf2f7;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.add-product-btn {
  margin-top: 0.75rem;
}

.button-group {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.invalid-type-error {
  padding: 1.5rem;
  background: #fff5f5;
  border: 1px solid #fc8181;
  border-left: 4px solid #e53e3e;
  border-radius: 8px;
  color: #742a2a;
}

.invalid-type-error h3 {
  color: #c53030;
  margin-bottom: 0.75rem;
}

.invalid-type-error code {
  background: #fed7d7;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .type-list {
    grid-template-columns: 1fr;
  }

  .button-group {
    flex-direction: column;
  }

  .button-group button {
    width: 100%;
  }
}

.crop-filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.crop-filter-btn {
  padding: 0.25rem 0.6rem;
  font-size: 0.8rem;
  border: 1px solid var(--border-color, #ccc);
  border-radius: 4px;
  background: var(--bg-secondary, #f5f5f5);
  color: var(--text-color, #333);
  cursor: pointer;
  transition: all 0.15s ease;
}

.crop-filter-btn:hover {
  background: var(--bg-hover, #e8e8e8);
}

.crop-filter-btn.active {
  background: var(--primary-color, #4a90d9);
  color: white;
  border-color: var(--primary-color, #4a90d9);
}

.fields-table-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
}

.fields-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.fields-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.fields-table th {
  background: var(--bg-secondary, #f5f5f5);
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color, #ddd);
}

.fields-table td {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--border-color, #eee);
}

.fields-table tbody tr {
  cursor: pointer;
  transition: background 0.1s ease;
}

.fields-table tbody tr:hover {
  background: var(--bg-hover, #f0f0f0);
}

.fields-table tbody tr.selected {
  background: rgba(74, 144, 217, 0.1);
}

.col-check {
  width: 36px;
  text-align: center;
}

.col-area {
  width: 80px;
  text-align: right;
}

.fields-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary, #666);
}

.area-total {
  color: var(--primary-color, #4a90d9);
}

/* Trappolaggio */
.trap-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.trap-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.trap-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary, #2d3748);
  margin-bottom: 0.4rem;
}

.trap-select-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.trap-select-row .form-control {
  flex: 1 1 0;
  min-width: 0;
}

.trap-select-row > :not(.form-control) {
  flex: 0 0 auto;
}

.trap-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.trap-option {
  display: flex;
  flex-direction: column;
  padding: 0.6rem 1rem;
  border: 2px solid var(--border-color, #ddd);
  border-radius: 8px;
  cursor: pointer;
  background: white;
  transition: all 0.15s ease;
  min-width: 140px;
}

.trap-option:hover {
  border-color: var(--primary-color, #4a90d9);
}

.trap-option.selected {
  border-color: var(--primary-color, #4a90d9);
  background: rgba(74, 144, 217, 0.08);
}

.trap-option-new {
  color: var(--primary-color, #4a90d9);
  font-weight: 600;
  align-items: center;
  justify-content: center;
  border-style: dashed;
}

.trap-type {
  font-size: 0.8rem;
  color: #718096;
  margin-top: 0.2rem;
}

.trap-pest {
  font-size: 0.78rem;
  color: #a0aec0;
  font-style: italic;
}

.new-trap-form {
  margin-top: 0.75rem;
  padding: 1rem;
  background: white;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
</style>
